// src/lib/master-wizard-to-diagnostic-mapper.ts
// CRITICAL FIX: Maps Master Wizard fields to Diagnostic Question IDs
// This ensures calculateDiagnosticScoring gets real answers instead of defaults

import type { AnswersMap } from "./master-engine-input";

/**
 * Maps Master Wizard raw answers to Diagnostic Question IDs
 * 
 * Master Wizard stores fields like:
 * - monthlyContacts, leadQuality, responseTime, etc.
 * 
 * Diagnostic System expects IDs like:
 * - ctx-001, dem-001, web-001, sal-001, pos-001, rep-001
 * 
 * This mapper bridges the gap.
 */
export function mapWizardAnswersToDiagnosticIds(rawAnswers: AnswersMap): Record<string, unknown> {
  const diagnosticAnswers: Record<string, unknown> = {};

  // -------------------------------------------------
  // 1. CONTEXT (ctx-001 to ctx-005)
  // -------------------------------------------------
  
  // ctx-001: Wie erklärungsbedürftig ist Ihr Angebot?
  if (rawAnswers.productComplexity) {
    diagnosticAnswers["ctx-001"] = rawAnswers.productComplexity;
  }

  // ctx-002: Wie lang ist Ihr typischer Sales Cycle?
  if (rawAnswers.salesCycleExact !== undefined && rawAnswers.salesCycleExact !== null) {
    const months = Number(rawAnswers.salesCycleExact);
    if (months <= 1) diagnosticAnswers["ctx-002"] = "1";
    else if (months <= 3) diagnosticAnswers["ctx-002"] = "2";
    else if (months <= 6) diagnosticAnswers["ctx-002"] = "3";
    else if (months <= 12) diagnosticAnswers["ctx-002"] = "4";
    else diagnosticAnswers["ctx-002"] = "5";
  }

  // ctx-003: Wie hoch ist Ihr durchschnittlicher Deal Value?
  if (rawAnswers.averageDealSizeExact !== undefined && rawAnswers.averageDealSizeExact !== null) {
    const dealSize = Number(rawAnswers.averageDealSizeExact);
    if (dealSize < 5000) diagnosticAnswers["ctx-003"] = "1";
    else if (dealSize < 20000) diagnosticAnswers["ctx-003"] = "2";
    else if (dealSize < 50000) diagnosticAnswers["ctx-003"] = "3";
    else if (dealSize < 100000) diagnosticAnswers["ctx-003"] = "4";
    else diagnosticAnswers["ctx-003"] = "5";
  }

  // ctx-004: Wie viele Entscheider sind typischerweise involviert?
  if (rawAnswers.decisionMakers) {
    diagnosticAnswers["ctx-004"] = rawAnswers.decisionMakers;
  }

  // ctx-005: Wie stark ist der Wettbewerb in Ihrem Markt?
  if (rawAnswers.competitionIntensity) {
    diagnosticAnswers["ctx-005"] = rawAnswers.competitionIntensity;
  }

  // -------------------------------------------------
  // 2. DEMAND (dem-001 to dem-007)
  // -------------------------------------------------

  // dem-001: Lead-Volumen — echte Zahl eintragen, scoreFromNumber rechnet korrekt
  if (rawAnswers.monthlyContactsExact !== undefined && rawAnswers.monthlyContactsExact !== null) {
    diagnosticAnswers["dem-001"] = Number(rawAnswers.monthlyContactsExact);
  }

  // dem-002: Leadqualität — qualifiedLeadRatePercent ist primäre Quelle, leadQuality als Fallback
  if (rawAnswers.qualifiedLeadRatePercentExact !== undefined && rawAnswers.qualifiedLeadRatePercentExact !== null) {
    const rate = Number(rawAnswers.qualifiedLeadRatePercentExact);
    if (rate >= 60) diagnosticAnswers["dem-002"] = "5";      // sehr hoch
    else if (rate >= 40) diagnosticAnswers["dem-002"] = "4";  // gut
    else if (rate >= 25) diagnosticAnswers["dem-002"] = "3";  // mittel
    else if (rate >= 10) diagnosticAnswers["dem-002"] = "2";  // schwach
    else diagnosticAnswers["dem-002"] = "1";                  // sehr schwach
  } else if (rawAnswers.leadQuality) {
    // Fallback: qualitative Einschätzung
    const lqMap: Record<string, string> = {
      "very-high": "5", good: "4", mixed: "3", weak: "2", "very-weak": "1",
    };
    const mapped = lqMap[rawAnswers.leadQuality as string];
    if (mapped) diagnosticAnswers["dem-002"] = mapped;
  }

  // dem-003: Lead-Quellen-Diversität (aus Akquise-Mix)
  if (rawAnswers.leadSourceDiversity) {
    diagnosticAnswers["dem-003"] = rawAnswers.leadSourceDiversity;
  } else {
    // Aus Akquise-Mix-Feldern berechnen
    const mixFields = [
      rawAnswers.acquisitionPhone, rawAnswers.acquisitionEmail, rawAnswers.acquisitionLinkedIn,
      rawAnswers.acquisitionXing, rawAnswers.acquisitionReferrals, rawAnswers.acquisitionEvents,
      rawAnswers.acquisitionContentMarketing, rawAnswers.acquisitionPaidAds,
      rawAnswers.acquisitionColdVisits, rawAnswers.acquisitionPartner, rawAnswers.acquisitionOther,
    ];
    const activeChannels = mixFields.filter(v => v !== undefined && Number(v) > 5).length;
    if (activeChannels >= 4) diagnosticAnswers["dem-003"] = "5";
    else if (activeChannels === 3) diagnosticAnswers["dem-003"] = "4";
    else if (activeChannels === 2) diagnosticAnswers["dem-003"] = "3";
    else if (activeChannels === 1) diagnosticAnswers["dem-003"] = "2";
    else diagnosticAnswers["dem-003"] = "1";
  }

  // dem-004: Lead-Angebots-Fit (aus pipelineSource + leadQuality als Proxy)
  // Primärquelle: qualifiedLeadRatePercent als Fit-Signal
  if (rawAnswers.qualifiedLeadRatePercentExact !== undefined && rawAnswers.qualifiedLeadRatePercentExact !== null) {
    const rate = Number(rawAnswers.qualifiedLeadRatePercentExact);
    if (rate >= 50) diagnosticAnswers["dem-004"] = "5";
    else if (rate >= 35) diagnosticAnswers["dem-004"] = "4";
    else if (rate >= 20) diagnosticAnswers["dem-004"] = "3";
    else if (rate >= 10) diagnosticAnswers["dem-004"] = "2";
    else diagnosticAnswers["dem-004"] = "1";
  } else if (rawAnswers.leadQuality) {
    const fitMap: Record<string, string> = {
      "very-high": "5", good: "4", mixed: "3", weak: "2", "very-weak": "1",
    };
    const mapped = fitMap[rawAnswers.leadQuality as string];
    if (mapped) diagnosticAnswers["dem-004"] = mapped;
  }

  // dem-005: Lead Scoring Maturity
  if (rawAnswers.hasLeadScoring !== undefined) {
    diagnosticAnswers["dem-005"] = rawAnswers.hasLeadScoring ? "yes" : "no";
  }

  // dem-006: ICP-Klarheit (NEU — war früher Reaktionszeit, jetzt DEMAND/POSITIONING)
  if (rawAnswers.icpClarity && rawAnswers.icpClarity !== "not-known") {
    diagnosticAnswers["dem-006"] = rawAnswers.icpClarity;
  } else if (rawAnswers.icpDefined && rawAnswers.icpDefined !== "not-known") {
    // Fallback: altes icpDefined-Feld
    const iMap: Record<string, string> = {
      yes: "mostly-clear", partly: "vague", no: "not-defined",
    };
    const mapped = iMap[rawAnswers.icpDefined as string];
    if (mapped) diagnosticAnswers["dem-006"] = mapped;
  }

  // dem-007: Marktpräsenz (NEU — direkt aus neuem Wizard-Feld)
  if (rawAnswers.marketPresence && rawAnswers.marketPresence !== "not-known") {
    diagnosticAnswers["dem-007"] = rawAnswers.marketPresence;
  }

  // -------------------------------------------------
  // 3. WEBSITE (web-001 to web-008)
  // -------------------------------------------------

  // web-001: Wie klar ist Ihr Website-Angebot?
  if (rawAnswers.websiteClarity) {
    diagnosticAnswers["web-001"] = rawAnswers.websiteClarity;
  }

  // web-002: Haben Sie klare CTAs?
  if (rawAnswers.hasCtaButtons !== undefined) {
    diagnosticAnswers["web-002"] = rawAnswers.hasCtaButtons ? "yes" : "no";
  }

  // web-003: Haben Sie Social Proof?
  if (rawAnswers.hasSocialProof !== undefined) {
    diagnosticAnswers["web-003"] = rawAnswers.hasSocialProof ? "yes" : "no";
  }

  // web-004: Ist Ihre Website mobil-optimiert?
  if (rawAnswers.isMobileOptimized !== undefined) {
    diagnosticAnswers["web-004"] = rawAnswers.isMobileOptimized ? "yes" : "no";
  }

  // web-005: Haben Sie ein Kontaktformular?
  if (rawAnswers.hasContactForm !== undefined) {
    diagnosticAnswers["web-005"] = rawAnswers.hasContactForm ? "yes" : "no";
  }

  // web-006: Haben Sie Live Chat?
  if (rawAnswers.hasLiveChat !== undefined) {
    diagnosticAnswers["web-006"] = rawAnswers.hasLiveChat ? "yes" : "no";
  }

  // web-007: Wie schnell lädt Ihre Website?
  if (rawAnswers.websiteSpeed) {
    diagnosticAnswers["web-007"] = rawAnswers.websiteSpeed;
  }

  // web-008: Tracken Sie Website-Conversions?
  if (rawAnswers.tracksConversions !== undefined) {
    diagnosticAnswers["web-008"] = rawAnswers.tracksConversions ? "yes" : "no";
  }

  // -------------------------------------------------
  // 4. SALES QUALITY (sal-001 to sal-010)
  // -------------------------------------------------

  // sal-001: Wie strukturiert sind Ihre Discovery Calls?
  if (rawAnswers.discoveryStructure) {
    diagnosticAnswers["sal-001"] = rawAnswers.discoveryStructure;
  }

  // sal-002: Nutzen Sie ein Sales-Playbook?
  if (rawAnswers.hasSalesPlaybook !== undefined) {
    diagnosticAnswers["sal-002"] = rawAnswers.hasSalesPlaybook ? "yes" : "no";
  }

  // sal-003: Wie gut qualifizieren Sie Opportunities?
  if (rawAnswers.opportunityQualification) {
    diagnosticAnswers["sal-003"] = rawAnswers.opportunityQualification;
  }

  // sal-004: Wie strukturiert ist Ihr Angebotsprozess?
  if (rawAnswers.proposalProcess) {
    diagnosticAnswers["sal-004"] = rawAnswers.proposalProcess;
  }

  // sal-005: Wie gut ist Ihr Follow-up?
  if (rawAnswers.followUpDiscipline) {
    diagnosticAnswers["sal-005"] = rawAnswers.followUpDiscipline;
  }

  // sal-006: Nutzen Sie Sales-Automation?
  if (rawAnswers.usesSalesAutomation !== undefined) {
    diagnosticAnswers["sal-006"] = rawAnswers.usesSalesAutomation ? "yes" : "no";
  }

  // sal-007: Wie gut ist Ihre Einwandbehandlung?
  if (rawAnswers.objectionHandling) {
    diagnosticAnswers["sal-007"] = rawAnswers.objectionHandling;
  }

  // sal-008: Nutzen Sie Storytelling?
  if (rawAnswers.usesStorytelling !== undefined) {
    diagnosticAnswers["sal-008"] = rawAnswers.usesStorytelling ? "yes" : "partly";
  }

  // sal-009: Wie gut ist Ihre Demo-Qualität?
  if (rawAnswers.demoQuality) {
    diagnosticAnswers["sal-009"] = rawAnswers.demoQuality;
  }

  // sal-010: Wie strukturiert ist Ihr Closing?
  if (rawAnswers.closingProcess) {
    diagnosticAnswers["sal-010"] = rawAnswers.closingProcess;
  }

  // -------------------------------------------------
  // 5. PROCESS (pro-001 to pro-010)
  // -------------------------------------------------

  // pro-001: Nutzen Sie ein CRM?
  if (rawAnswers.usesCrm !== undefined) {
    diagnosticAnswers["pro-001"] = rawAnswers.usesCrm ? "yes" : "no";
  }

  // pro-002: Wie transparent ist Ihre Pipeline?
  if (rawAnswers.pipelineTransparency) {
    diagnosticAnswers["pro-002"] = rawAnswers.pipelineTransparency;
  }

  // pro-003: Haben Sie definierte Sales Stages?
  if (rawAnswers.hasDefinedStages !== undefined) {
    diagnosticAnswers["pro-003"] = rawAnswers.hasDefinedStages ? "yes" : "no";
  }

  // pro-004: Wie oft reviewen Sie Ihre Pipeline?
  if (rawAnswers.pipelineReviewFrequency) {
    diagnosticAnswers["pro-004"] = rawAnswers.pipelineReviewFrequency;
  }

  // pro-005: Haben Sie Sales-Forecasting?
  if (rawAnswers.hasForecasting !== undefined) {
    diagnosticAnswers["pro-005"] = rawAnswers.hasForecasting ? "yes" : "no";
  }

  // pro-006: Tracken Sie Sales-KPIs?
  if (rawAnswers.tracksKpis !== undefined) {
    diagnosticAnswers["pro-006"] = rawAnswers.tracksKpis ? "yes" : "no";
  }

  // pro-007: Haben Sie Sales-Coaching?
  if (rawAnswers.hasCoaching !== undefined) {
    diagnosticAnswers["pro-007"] = rawAnswers.hasCoaching ? "yes" : "partly";
  }

  // pro-008: Wie strukturiert ist Ihr Onboarding?
  if (rawAnswers.onboardingStructure) {
    diagnosticAnswers["pro-008"] = rawAnswers.onboardingStructure;
  }

  // pro-009: Nutzen Sie Sales-Enablement?
  if (rawAnswers.usesEnablement !== undefined) {
    diagnosticAnswers["pro-009"] = rawAnswers.usesEnablement ? "yes" : "partly";
  }

  // pro-010: Wie gut ist Ihre Sales-Dokumentation?
  if (rawAnswers.documentationQuality) {
    diagnosticAnswers["pro-010"] = rawAnswers.documentationQuality;
  }

  // -------------------------------------------------
  // 6. POSITIONING (pos-001 to pos-010)
  // -------------------------------------------------

  // pos-001: Wie klar ist Ihre Value Proposition?
  if (rawAnswers.valuePropositionClarity) {
    diagnosticAnswers["pos-001"] = rawAnswers.valuePropositionClarity;
  }

  // pos-002: Wie differenziert sind Sie vom Wettbewerb?
  if (rawAnswers.differentiation) {
    diagnosticAnswers["pos-002"] = rawAnswers.differentiation;
  }

  // pos-003: Wie klar ist Ihre Zielgruppe definiert?
  if (rawAnswers.targetAudienceDefinition) {
    diagnosticAnswers["pos-003"] = rawAnswers.targetAudienceDefinition;
  }

  // pos-004: Haben Sie Case Studies?
  if (rawAnswers.hasCaseStudies !== undefined) {
    diagnosticAnswers["pos-004"] = rawAnswers.hasCaseStudies ? "yes" : "no";
  }

  // pos-005: Wie stark ist Ihre Brand Awareness?
  if (rawAnswers.brandAwareness) {
    diagnosticAnswers["pos-005"] = rawAnswers.brandAwareness;
  }

  // pos-006: Nutzen Sie Thought Leadership?
  if (rawAnswers.usesThoughtLeadership !== undefined) {
    diagnosticAnswers["pos-006"] = rawAnswers.usesThoughtLeadership ? "yes" : "partly";
  }

  // pos-007: Wie konsistent ist Ihr Messaging?
  if (rawAnswers.messagingConsistency) {
    diagnosticAnswers["pos-007"] = rawAnswers.messagingConsistency;
  }

  // pos-008: Haben Sie eine klare Pricing-Strategie?
  if (rawAnswers.pricingClarity) {
    diagnosticAnswers["pos-008"] = rawAnswers.pricingClarity;
  }

  // pos-009: Wie gut kommunizieren Sie ROI?
  if (rawAnswers.roiCommunication) {
    diagnosticAnswers["pos-009"] = rawAnswers.roiCommunication;
  }

  // pos-010: Haben Sie Referenzkunden?
  if (rawAnswers.hasReferenceCustomers !== undefined) {
    diagnosticAnswers["pos-010"] = rawAnswers.hasReferenceCustomers ? "yes" : "no";
  }

  // -------------------------------------------------
  // 7. REP PERFORMANCE (rep-001 to rep-010)
  // -------------------------------------------------

  // rep-001: Wie viele produktive Reps haben Sie?
  if (rawAnswers.productiveRepsExact !== undefined && rawAnswers.productiveRepsExact !== null) {
    const reps = Number(rawAnswers.productiveRepsExact);
    if (reps < 3) diagnosticAnswers["rep-001"] = "1";
    else if (reps < 5) diagnosticAnswers["rep-001"] = "2";
    else if (reps < 10) diagnosticAnswers["rep-001"] = "3";
    else if (reps < 20) diagnosticAnswers["rep-001"] = "4";
    else diagnosticAnswers["rep-001"] = "5";
  }

  // rep-002: Wie hoch ist die durchschnittliche Zielerreichung?
  if (rawAnswers.avgGoalAttainmentExact !== undefined && rawAnswers.avgGoalAttainmentExact !== null) {
    const attainment = Number(rawAnswers.avgGoalAttainmentExact);
    if (attainment < 50) diagnosticAnswers["rep-002"] = "1";
    else if (attainment < 70) diagnosticAnswers["rep-002"] = "2";
    else if (attainment < 90) diagnosticAnswers["rep-002"] = "3";
    else if (attainment < 110) diagnosticAnswers["rep-002"] = "4";
    else diagnosticAnswers["rep-002"] = "5";
  }

  // rep-003: Wie groß ist die Performance-Streuung?
  if (rawAnswers.performanceVariance) {
    diagnosticAnswers["rep-003"] = rawAnswers.performanceVariance;
  }

  // rep-004: Wie hoch ist die Rep-Fluktuation?
  if (rawAnswers.repChurnRate) {
    diagnosticAnswers["rep-004"] = rawAnswers.repChurnRate;
  }

  // rep-005: Wie strukturiert ist Ihr Coaching?
  if (rawAnswers.coachingStructure) {
    diagnosticAnswers["rep-005"] = rawAnswers.coachingStructure;
  }

  // rep-006: Nutzen Sie Performance-Dashboards?
  if (rawAnswers.usesPerformanceDashboards !== undefined) {
    diagnosticAnswers["rep-006"] = rawAnswers.usesPerformanceDashboards ? "yes" : "no";
  }

  // rep-007: Wie gut ist Ihre Pipeline-Hygiene?
  if (rawAnswers.pipelineHygiene) {
    diagnosticAnswers["rep-007"] = rawAnswers.pipelineHygiene;
  }

  // rep-008: Haben Sie klare Activity-Targets?
  if (rawAnswers.hasActivityTargets !== undefined) {
    diagnosticAnswers["rep-008"] = rawAnswers.hasActivityTargets ? "yes" : "no";
  }

  // rep-009: Wie gut ist Ihre Win-Rate?
  if (rawAnswers.winRateQuality) {
    diagnosticAnswers["rep-009"] = rawAnswers.winRateQuality;
  }

  // rep-010: Nutzen Sie Sales-Gamification?
  if (rawAnswers.usesGamification !== undefined) {
    diagnosticAnswers["rep-010"] = rawAnswers.usesGamification ? "yes" : "no";
  }

  // -------------------------------------------------
  // 8. EXTENDED FIELD MAPPINGS
  // -------------------------------------------------

  // dem-005 (lead scoring): leadScoringMaturity hat Priorität über hasLeadScoring
  if (rawAnswers.leadScoringMaturity && rawAnswers.leadScoringMaturity !== "not-known") {
    const lsMap: Record<string, string> = {
      formal: "yes",
      informal: "partly",
      none: "no",
    };
    const mapped = lsMap[rawAnswers.leadScoringMaturity as string];
    // Override — leadScoringMaturity ist präziser als hasLeadScoring boolean
    if (mapped) diagnosticAnswers["dem-005"] = mapped;
  }

  // dem-002 / dem-004 Primärquelle ist bereits oben gesetzt (qualifiedLeadRatePercentExact).
  // Kein weiteres Override hier nötig — vermeidet Konflikte mit der neuen Logik.

  // sal-001 (discovery structure): also use discoveryFrameworkStep3
  if (rawAnswers.discoveryFrameworkStep3 && rawAnswers.discoveryFrameworkStep3 !== "not-known") {
    if (!diagnosticAnswers["sal-001"]) {
      diagnosticAnswers["sal-001"] =
        rawAnswers.discoveryFrameworkStep3 === "yes"
          ? "very-structured"
          : rawAnswers.discoveryFrameworkStep3 === "partly"
          ? "partly-structured"
          : "unstructured";
    }
  }

  // sal-001: No-show rate as negative discovery signal
  if (rawAnswers.discoveryNoShowRatePercentExact !== undefined && rawAnswers.discoveryNoShowRatePercentExact !== null) {
    const noShow = Number(rawAnswers.discoveryNoShowRatePercentExact);
    if (!diagnosticAnswers["sal-001"] && noShow > 25) {
      diagnosticAnswers["sal-001"] = "unstructured";
    }
  }

  // sal-004 (proposal process): Use daysFromDiscoveryToProposal and proposalsWithNextStepPercent
  if (rawAnswers.daysFromDiscoveryToProposalExact !== undefined && rawAnswers.daysFromDiscoveryToProposalExact !== null) {
    const days = Number(rawAnswers.daysFromDiscoveryToProposalExact);
    if (!diagnosticAnswers["sal-004"]) {
      if (days <= 3) diagnosticAnswers["sal-004"] = "very-structured";
      else if (days <= 7) diagnosticAnswers["sal-004"] = "structured";
      else if (days <= 14) diagnosticAnswers["sal-004"] = "partly-structured";
      else diagnosticAnswers["sal-004"] = "unstructured";
    }
  }
  if (rawAnswers.proposalsWithNextStepPercentExact !== undefined && rawAnswers.proposalsWithNextStepPercentExact !== null) {
    const pct = Number(rawAnswers.proposalsWithNextStepPercentExact);
    if (pct < 30 && (!diagnosticAnswers["sal-004"] || diagnosticAnswers["sal-004"] === "very-structured")) {
      diagnosticAnswers["sal-004"] = "partly-structured";
    }
  }

  // pro-002 (pipeline transparency): also use stalePipelineSharePercent
  if (rawAnswers.stalePipelineSharePercentExact !== undefined && rawAnswers.stalePipelineSharePercentExact !== null) {
    const stale = Number(rawAnswers.stalePipelineSharePercentExact);
    if (!diagnosticAnswers["pro-002"]) {
      if (stale < 10) diagnosticAnswers["pro-002"] = "full";
      else if (stale < 25) diagnosticAnswers["pro-002"] = "partial";
      else if (stale < 40) diagnosticAnswers["pro-002"] = "low";
      else diagnosticAnswers["pro-002"] = "minimal";
    }
  }

  // pro-005 (forecasting): also use forecastCoverageRatio
  if (rawAnswers.forecastCoverageRatioExact !== undefined && rawAnswers.forecastCoverageRatioExact !== null) {
    const ratio = Number(rawAnswers.forecastCoverageRatioExact);
    if (!diagnosticAnswers["pro-005"]) {
      diagnosticAnswers["pro-005"] = ratio >= 2.5 ? "yes" : ratio >= 1.5 ? "partly" : "no";
    }
  }

  // ctx-004 (decision makers): also use decisionMakerInProcess
  if (rawAnswers.decisionMakerInProcess && rawAnswers.decisionMakerInProcess !== "not-known") {
    if (!diagnosticAnswers["ctx-004"]) {
      const dmMap: Record<string, string> = {
        "first-meeting": "1",
        "mid-process": "2",
        "late-process": "4",
        "never": "5",
      };
      const mapped = dmMap[rawAnswers.decisionMakerInProcess as string];
      if (mapped) diagnosticAnswers["ctx-004"] = mapped;
    }
  }

  // sal-005 (follow-up): also use avgFollowUpsPerLead
  if (rawAnswers.avgFollowUpsPerLeadExact !== undefined && rawAnswers.avgFollowUpsPerLeadExact !== null) {
    const fuCount = Number(rawAnswers.avgFollowUpsPerLeadExact);
    if (!diagnosticAnswers["sal-005"]) {
      if (fuCount >= 6) diagnosticAnswers["sal-005"] = "very-structured";
      else if (fuCount >= 4) diagnosticAnswers["sal-005"] = "partly-structured";
      else if (fuCount >= 2) diagnosticAnswers["sal-005"] = "ad-hoc";
      else diagnosticAnswers["sal-005"] = "hardly-any";
    }
  }

  return diagnosticAnswers;
}